<script lang="ts">
  /**
   * Блок коментарів під матеріалом: форма, стрічка, підвантаження, модалки.
   *
   * Монтується лише коли матеріал відкритий користувачеві (див. MaterialPage) —
   * під закритим уроком обговорення не показуємо взагалі, і сервер віддає на
   * нього 403. Живе ПОЗА обгорткою .no-copy, інакше поле вводу було б
   * непридатним у Telegram WebView.
   */
  import { onMount } from 'svelte';
  import CommentForm from './CommentForm.svelte';
  import CommentItem from './CommentItem.svelte';
  import CommentReportModal from './CommentReportModal.svelte';
  import ConfirmModal from './ConfirmModal.svelte';
  import Icon from './Icon.svelte';
  import { api, ApiError } from '../lib/api';
  import { pluralUa } from '../lib/format';
  import type { CommentDTO, CommentReportReason } from '../lib/types';

  interface Props {
    materialId: string;
    /** false для адміна в режимі редактора — він переглядає, а не спілкується. */
    canWrite?: boolean;
  }
  let { materialId, canWrite = true }: Props = $props();

  let items = $state<CommentDTO[]>([]);
  let total = $state(0);
  let page = $state(1);
  let hasMore = $state(false);
  let loading = $state(true);
  let loadingMore = $state(false);
  let submitting = $state(false);
  let error = $state<string | null>(null);
  let replyTo = $state<CommentDTO | null>(null);
  let deleteTarget = $state<CommentDTO | null>(null);
  let reportTarget = $state<CommentDTO | null>(null);
  // Шторка скарги лишається відкритою після успіху — показує «Дякуємо», поки
  // людина сама не закриє. Тому окремий прапорець, а не просто reportTarget=null.
  let reportSubmitted = $state(false);
  let reportError = $state<string | null>(null);
  let busy = $state(false);

  /** Плаский перебір стрічки — дії приходять і від коренів, і від відповідей. */
  function forEachComment(fn: (c: CommentDTO) => void): void {
    for (const root of items) {
      fn(root);
      for (const reply of root.replies) fn(reply);
    }
  }

  async function load(opts: { append?: boolean } = {}): Promise<void> {
    if (opts.append) loadingMore = true;
    else loading = true;
    error = null;
    try {
      const nextPage = opts.append ? page + 1 : 1;
      const res = await api.materialComments(materialId, { page: nextPage });
      items = opts.append ? [...items, ...res.items] : res.items;
      page = res.page;
      total = res.total;
      hasMore = res.hasMore;
    } catch (err) {
      // 403 тут означає, що доступ зник між рендером сторінки і запитом.
      // Мовчки ховаємо блок, а не лякаємо помилкою.
      if (err instanceof ApiError && err.status === 403) {
        items = [];
        hasMore = false;
      } else {
        error = 'Не вдалось завантажити коментарі';
      }
    } finally {
      loading = false;
      loadingMore = false;
    }
  }

  onMount(load);

  async function submit(text: string): Promise<void> {
    submitting = true;
    error = null;
    const parentId = replyTo?.id ?? null;
    try {
      const created = await api.createComment({ materialId, text, parentId });
      if (parentId) {
        const parent = items.find((c) => c.id === parentId);
        if (parent) parent.replies = [...parent.replies, created];
      } else {
        // Нові зверху — так само, як їх сортує сервер.
        items = [created, ...items];
      }
      total += 1;
      replyTo = null;
    } catch (err) {
      error =
        err instanceof ApiError && err.code === 'rate_limited'
          ? 'Занадто багато коментарів поспіль. Спробуйте за хвилину.'
          : 'Не вдалось надіслати коментар';
    } finally {
      submitting = false;
    }
  }

  /** Оптимістичний перемикач: фліпаємо локально, відкочуємо на помилці. */
  async function toggleLike(target: CommentDTO): Promise<void> {
    const before = { liked: target.likedByMe, count: target.likeCount };
    target.likedByMe = !before.liked;
    target.likeCount = before.count + (before.liked ? -1 : 1);
    try {
      const res = await api.toggleCommentLike(target.id);
      target.likedByMe = res.liked;
      target.likeCount = res.likeCount;
    } catch {
      target.likedByMe = before.liked;
      target.likeCount = before.count;
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!deleteTarget) return;
    const target = deleteTarget;
    busy = true;
    try {
      await api.deleteComment(target.id);
      if (target.parentId) {
        const parent = items.find((c) => c.id === target.parentId);
        if (parent) parent.replies = parent.replies.filter((r) => r.id !== target.id);
        total -= 1;
      } else {
        // Видалення кореня забирає і його гілку — так само, як на сервері.
        total -= 1 + target.replies.length;
        items = items.filter((c) => c.id !== target.id);
      }
      if (replyTo?.id === target.id) replyTo = null;
    } catch {
      error = 'Не вдалось видалити коментар';
    } finally {
      busy = false;
      deleteTarget = null;
    }
  }

  async function submitReport(reason: CommentReportReason): Promise<void> {
    if (!reportTarget) return;
    const id = reportTarget.id;
    busy = true;
    reportError = null;
    try {
      await api.reportComment(id, { reason });
      forEachComment((c) => {
        if (c.id === id) c.reportedByMe = true;
      });
      reportSubmitted = true;
    } catch (err) {
      // Помилку показуємо в самій шторці й лишаємо крок підтвердження — щоб
      // повторна спроба не вимагала знову шукати причину в списку.
      reportError =
        err instanceof ApiError && err.code === 'rate_limited'
          ? 'Забагато скарг поспіль. Спробуйте пізніше.'
          : 'Не вдалось надіслати скаргу';
    } finally {
      busy = false;
    }
  }

  function closeReport(): void {
    reportTarget = null;
    reportSubmitted = false;
    reportError = null;
  }
</script>

<section class="comments">
  <h2 class="title">
    <Icon name="message-circle" size="lg" />
    <!-- Рахунок через вираз, а не {#if}: Svelte зрізає пробіл на межі блоку,
         і виходило «Коментарі(6)» замість «Коментарі (6)». -->
    {total > 0 ? `Коментарі (${total})` : 'Коментарі'}
  </h2>

  {#if canWrite}
    <CommentForm
      {submitting}
      replyingToName={replyTo?.author.name ?? null}
      onSubmit={submit}
      onCancelReply={() => (replyTo = null)}
    />
  {/if}

  {#if error}
    <div class="error">{error}</div>
  {/if}

  {#if loading}
    <div class="hint">Завантажуємо…</div>
  {:else if items.length === 0}
    <div class="hint">
      {canWrite ? 'Ще немає коментарів. Напишіть перший.' : 'Ще немає коментарів.'}
    </div>
  {:else}
    <div class="list">
      {#each items as comment (comment.id)}
        <CommentItem
          {comment}
          {canWrite}
          onLike={toggleLike}
          onReply={(c) => (replyTo = c)}
          onDelete={(c) => (deleteTarget = c)}
          onReport={(c) => (reportTarget = c)}
        />
      {/each}
    </div>

    {#if hasMore}
      <button type="button" class="more" disabled={loadingMore} onclick={() => load({ append: true })}>
        {loadingMore ? 'Завантажуємо…' : 'Показати ще'}
      </button>
    {/if}
  {/if}
</section>

<ConfirmModal
  open={deleteTarget !== null}
  title="Видалити коментар?"
  message={deleteTarget && deleteTarget.replies.length > 0
    ? `Разом із ним зникнуть і ${deleteTarget.replies.length} ${pluralUa(deleteTarget.replies.length, 'відповідь', 'відповіді', 'відповідей')} на нього.`
    : 'Коментар зникне зі сторінки матеріалу.'}
  confirmLabel={busy ? 'Видаляємо…' : 'Так, видалити'}
  onConfirm={confirmDelete}
  onCancel={() => (deleteTarget = null)}
/>

<CommentReportModal
  open={reportTarget !== null}
  submitting={busy}
  submitted={reportSubmitted}
  errorText={reportError}
  onSubmit={submitReport}
  onCancel={closeReport}
/>

<style>
  .comments {
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid var(--border);
  }
  .title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 600;
    color: var(--text);
    margin: 0 0 12px;
  }
  .list {
    margin-top: 8px;
  }
  .list > :global(.comment + .comment) {
    border-top: 1px solid var(--border);
  }
  .hint {
    padding: 16px 0;
    font-size: 13px;
    color: var(--faint);
  }
  .error {
    margin-top: 10px;
    font-size: 13px;
    color: var(--status-inactive);
  }
  .more {
    width: 100%;
    margin-top: 12px;
    padding: 10px;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 10px;
    font-family: inherit;
    font-size: 13px;
    color: var(--text);
    cursor: pointer;
  }
  .more:disabled {
    opacity: 0.6;
    cursor: default;
  }
</style>
